/**
 * Админ Dashboard — полная аналитика
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminStats, checkAdminAccess, AdminStats } from '../../lib/admin-api';
import AdminLogin from './AdminLogin';
import AdminShell from './AdminShell';
import { isCoursesSite } from '../../platform/detect';

interface Analytics {
  play: {
    roomsCreated: number;
    gamesStarted: number;
    aiRequests: number;
    aiErrors: number;
    errorRate: number;
    topTopics: Array<{ name: string; count: number }>;
  };
  swipe: {
    sessions: number;
    sessionsToday: number;
    questions: number;
    correct: number;
    correctRate: number;
    avgQuestionsPerSession: number;
    usersTotal: number;
    usersToday: number;
    topCategories: Array<{ name: string; count: number }>;
    topTags: Array<{ name: string; count: number }>;
  };
  collection: {
    started: number;
    completed: number;
    completionRate: number;
    lessonsCompleted: number;
    users: number;
    topCourses: Array<{ name: string; count: number }>;
  };
  errors: Array<{ time: string; error: string; topic?: string }>;
  uptime: string;
}

async function getAnalytics(): Promise<Analytics> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const tg = (window as any).Telegram?.WebApp;
  if (tg?.initDataUnsafe?.user?.id) headers['X-Telegram-User-Id'] = String(tg.initDataUnsafe.user.id);
  if (tg?.initData) headers['X-Telegram-Init-Data'] = tg.initData;

  const res = await fetch('/api/admin/analytics', { headers });
  if (!res.ok) throw new Error('Failed');
  const data = await res.json();
  return data.analytics;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [recentCourses, setRecentCourses] = useState<any[]>([]);
  // На витрине курсов игровая часть не нужна — она только мешает
  const coursesOnly = isCoursesSite();
  const [tab, setTab] = useState<'overview' | 'play' | 'swipe' | 'collections' | 'errors'>(
    coursesOnly ? 'collections' : 'overview'
  );
  // Нет доступа — показываем поле пароля, а не тупик «Доступ запрещён»
  const [needLogin, setNeedLogin] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const init = async () => {
      try {
        const isAdmin = await checkAdminAccess();
        if (!isAdmin) {
          setNeedLogin(true);
          return;
        }
        setNeedLogin(false);

        const [statsData, analyticsData] = await Promise.all([
          getAdminStats(),
          getAnalytics().catch(() => null),
        ]);

        setStats(statsData.stats);
        setRecentCourses(statsData.recentCourses);
        setAnalytics(analyticsData);
      } catch {
        setError('Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [reload]);

  const refreshAnalytics = async () => {
    try {
      const data = await getAnalytics();
      setAnalytics(data);
    } catch { /* ignore */ }
  };

  if (needLogin) {
    return <AdminLogin onSuccess={() => { setLoading(true); setNeedLogin(false); setReload((n) => n + 1); }} />;
  }

  if (loading) {
    return (
      <AdminShell title="Админка BESHEK" onBack={() => navigate('/')}>
        <div className="flex min-h-[50vh] items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#0F766E] border-t-transparent rounded-full animate-spin" />
      </div>
      </AdminShell>
    );
  }

  if (error) {
    return (
      <AdminShell title="Админка BESHEK" onBack={() => navigate('/')}>
        <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🚫</div>
          <p className="text-[#DC2626]">{error}</p>
        </div>
      </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Админка BESHEK" onBack={() => navigate('/')} actions={<>
      {analytics && <span className="text-xs text-[#626B73]">⏱ {analytics.uptime}</span>}
      <button onClick={refreshAnalytics} title="Обновить аналитику" aria-label="Обновить аналитику" className="rounded-lg bg-[#E5F2EF] px-3 py-2 text-[#0F766E]">🔄</button>
    </>}>
      {/* Вкладки */}
      <div className="bg-white border border-[#E1E5E3] border-b px-4">
        <div className="w-full flex gap-1 overflow-x-auto">
          {([
            { id: 'overview', label: '📊 Обзор' },
            { id: 'play', label: '🎮 Play' },
            { id: 'swipe', label: '⚡ Swipe' },
            { id: 'collections', label: '📚 Курсы' },
            { id: 'errors', label: '🐛 Ошибки' },
          ] as const)
            .filter(t => !coursesOnly || t.id === 'collections' || t.id === 'errors')
            .map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id
                  ? 'border-[#0F766E] bg-[#E5F2EF] text-[#0F766E]'
                  : 'border-transparent text-[#626B73] hover:text-[#626B73]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="py-6">
        {tab === 'overview' && (
          <OverviewTab stats={stats} analytics={analytics} recentCourses={recentCourses} navigate={navigate} />
        )}
        {tab === 'play' && analytics && <PlayTab data={analytics.play} />}
        {tab === 'swipe' && analytics && <SwipeTab data={analytics.swipe} />}
        {tab === 'collections' && analytics && (
          <CollectionsTab data={analytics.collection} navigate={navigate} />
        )}
        {tab === 'errors' && analytics && <ErrorsTab errors={analytics.errors} />}
        {!analytics && tab !== 'overview' && (
          <div className="text-center py-12 text-[#626B73]">Аналитика недоступна</div>
        )}
      </main>
    </AdminShell>
  );
}

// ==================== Tabs ====================

function OverviewTab({
  stats,
  analytics,
  recentCourses,
  navigate,
}: {
  stats: AdminStats | null;
  analytics: Analytics | null;
  recentCourses: any[];
  navigate: (path: string) => void;
}) {
  return (
    <div className="space-y-6">
      {/* Активность */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon="🎮" value={analytics.play.roomsCreated} label="Комнат /play" sub={`${analytics.play.gamesStarted} игр`} />
          <StatCard icon="⚡" value={analytics.swipe.sessions} label="Сессий /swipe" sub={`Сегодня: ${analytics.swipe.sessionsToday}`} />
          <StatCard icon="📚" value={analytics.collection.started} label="Начато курсов" sub={`${analytics.collection.completed} завершено`} />
          <StatCard icon="👥" value={analytics.swipe.usersTotal + analytics.collection.users} label="Пользователей" sub={`Swipe: ${analytics.swipe.usersToday} сегодня`} />
        </div>
      )}

      {/* Контент */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Контент</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard icon="📚" value={stats?.totalCourses || 0} label="Курсов" />
          <StatCard icon="📝" value={stats?.totalLessons || 0} label="Уроков" />
          <StatCard icon="📋" value={stats?.totalModules || 0} label="Модулей" />
          <StatCard icon="❓" value={stats?.totalQuestions || 0} label="Вопросов" />
          <StatCard icon="✅" value={stats?.publishedCourses || 0} label="Опубликовано" />
          <StatCard icon="📝" value={stats?.draftCourses || 0} label="Черновиков" />
        </div>
      </div>

      {/* Действия */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Быстрые действия</h2>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => navigate('/admin/courses')} className="px-4 py-2 bg-[#0F766E] text-white rounded-lg hover:bg-[#0B5F59] transition">
            📚 Управление курсами
          </button>
          <button onClick={() => navigate('/admin/courses/new')} className="px-4 py-2 bg-[#0F766E] text-white rounded-lg hover:bg-[#0B5F59] transition">
            + Создать курс
          </button>
        </div>
      </div>

      {/* Последние курсы */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Последние курсы</h2>
        <div className="space-y-2">
          {recentCourses.map(course => (
            <div
              key={course.id}
              className="bg-white border border-[#E1E5E3] rounded-lg p-4 flex flex-wrap gap-2 items-center justify-between cursor-pointer hover:bg-[#E5F2EF]"
              onClick={() => navigate(`/admin/courses/${course.id}`)}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{course.icon}</span>
                <div>
                  <div className="font-medium">{course.name}</div>
                  <div className="text-sm text-[#626B73]">
                    {course.isPublished ? '✅ Опубликован' : '📝 Черновик'}
                  </div>
                </div>
              </div>
              <span className="text-[#626B73]">→</span>
            </div>
          ))}
          {recentCourses.length === 0 && (
            <p className="text-[#626B73] text-center py-8">Курсов пока нет</p>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayTab({ data }: { data: Analytics['play'] }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon="🏠" value={data.roomsCreated} label="Комнат создано" />
        <StatCard icon="🎮" value={data.gamesStarted} label="Игр сыграно" />
        <StatCard icon="🤖" value={data.aiRequests} label="AI запросов" />
        <StatCard icon="❌" value={data.aiErrors} label="AI ошибок" color={data.errorRate > 10 ? 'red' : undefined} sub={`${data.errorRate}% ошибок`} />
      </div>

      {data.topTopics.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Топ темы</h3>
          <TopList items={data.topTopics} />
        </div>
      )}
    </div>
  );
}

function SwipeTab({ data }: { data: Analytics['swipe'] }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon="⚡" value={data.sessions} label="Всего сессий" sub={`Сегодня: ${data.sessionsToday}`} />
        <StatCard icon="❓" value={data.questions} label="Вопросов отвечено" sub={`~${data.avgQuestionsPerSession} за сессию`} />
        <StatCard icon="✅" value={data.correct} label="Правильных" sub={`${data.correctRate}% точность`} color={data.correctRate >= 50 ? 'green' : 'yellow'} />
        <StatCard icon="👥" value={data.usersTotal} label="Пользователей" sub={`Сегодня: ${data.usersToday}`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.topCategories.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3">Топ категории</h3>
            <TopList items={data.topCategories} />
          </div>
        )}
        {data.topTags.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3">Топ теги</h3>
            <TopList items={data.topTags} />
          </div>
        )}
      </div>
    </div>
  );
}

function CollectionsTab({
  data,
  navigate,
}: {
  data: Analytics['collection'];
  navigate: (path: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon="🚀" value={data.started} label="Начато" />
        <StatCard icon="🏆" value={data.completed} label="Завершено" sub={`${data.completionRate}% доходимость`} color={data.completionRate >= 50 ? 'green' : 'yellow'} />
        <StatCard icon="📖" value={data.lessonsCompleted} label="Уроков пройдено" />
        <StatCard icon="👥" value={data.users} label="Студентов" />
      </div>

      {data.topCourses.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Популярные курсы</h3>
          <TopList items={data.topCourses} />
        </div>
      )}

      <div>
        <button onClick={() => navigate('/admin/courses')} className="px-4 py-2 bg-[#0F766E] text-white rounded-lg hover:bg-[#0B5F59] transition">
          📚 Управление курсами →
        </button>
      </div>
    </div>
  );
}

function ErrorsTab({ errors }: { errors: Analytics['errors'] }) {
  return (
    <div>
      <h3 className="font-semibold mb-3">Последние ошибки</h3>
      {errors.length === 0 ? (
        <div className="text-center py-12 text-[#626B73]">
          <div className="text-4xl mb-2">✅</div>
          <p>Ошибок нет</p>
        </div>
      ) : (
        <div className="space-y-2">
          {errors.map((err, i) => (
            <div key={i} className="bg-white border border-[#E1E5E3] rounded-lg p-3">
              <div className="flex flex-wrap gap-2 items-center justify-between mb-1">
                <span className="text-xs text-[#DC2626] font-mono">{typeof err === 'string' ? err : err.error?.slice(0, 80)}</span>
                <span className="text-xs text-[#626B73]">{typeof err !== 'string' && err.time ? new Date(err.time).toLocaleString('ru') : ''}</span>
              </div>
              {typeof err !== 'string' && err.topic && (
                <div className="text-xs text-[#626B73]">Тема: {err.topic}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== Components ====================

function StatCard({
  icon,
  value,
  label,
  sub,
  color,
}: {
  icon: string;
  value: number;
  label: string;
  sub?: string;
  color?: 'green' | 'yellow' | 'red';
}) {
  const colorClass = color === 'green' ? 'text-[#0F766E]' : color === 'yellow' ? 'text-amber-700' : color === 'red' ? 'text-[#DC2626]' : 'text-[#252A2D]';

  return (
    <div className="bg-white border border-[#E1E5E3] rounded-lg p-4">
      <div className="text-2xl mb-1">{icon}</div>
      <div className={`text-2xl font-bold ${colorClass}`}>{value.toLocaleString('ru')}</div>
      <div className="text-sm text-[#626B73]">{label}</div>
      {sub && <div className="text-xs text-[#626B73] mt-1">{sub}</div>}
    </div>
  );
}

function TopList({ items }: { items: Array<{ name: string; count: number }> }) {
  const maxCount = items.length > 0 ? items[0].count : 1;

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={item.name} className="flex items-center gap-3">
          <span className="text-sm text-[#626B73] w-5">{i + 1}.</span>
          <div className="flex-1">
            <div className="flex flex-wrap gap-2 items-center justify-between mb-1">
              <span className="text-sm font-medium">{item.name}</span>
              <span className="text-sm text-[#626B73]">{item.count}</span>
            </div>
            <div className="h-1.5 bg-white border border-[#E1E5E3] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#0F766E] rounded-full"
                style={{ width: `${(item.count / maxCount) * 100}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}